import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import Swal from "sweetalert2";
import {
  getAllCareerApplications,
  getCareerApplicationById,
  deleteCareerApplication,
} from "../../services/Contact/careerApplicationServices";
import { calculateTotalPages, BootstrapTooltip } from "../../assets/js/script";
import TableHeader from "../Common/TableComponent/TableHeader";
import EntriesDropdown from "../Common/TableComponent/EntriesDropdown";
import { Pagination } from "../Common/TableComponent/Pagination";
import { Loading } from "../Common/OtherElements/Loading";
import { TableDataStatusError } from "../Common/OtherElements/TableDataStatusError";
import { confirmDelete } from "../Common/OtherElements/confirmDeleteClone";
import { handleErrors } from "../../utils/errorHandler";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";
import { resolveFileUrl } from "../../utils/fileUrlHelper";
import { blsDatePickerOptions } from "../../utils/blsDatePicker";
import { escapeHtml, externalLink, showDetailModal } from "../../utils/detailModal";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const resumeHref = (url) => {
  const resolved = resolveFileUrl(url);
  return /^https?:\/\//i.test(resolved) ? resolved : "";
};

export const ManageCareerApplication = () => {
  const navigate = useNavigate();
  const PageLevelAccessurl = "career-application";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  const [pageAccessDetails, setPageAccessDetails] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [applications, setApplications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getAllCareerApplications({
          pageNo: currentPage,
          pageSize: entriesPerPage,
          fromDate: fromDate || null,
          toDate: toDate || null,
          search,
        });
        const rows = Array.isArray(data) ? data : [];
        setApplications(rows);
        setTotalCount(rows[0]?.totalCount || 0);
      } catch (error) {
        setApplications([]);
        setTotalCount(0);
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, entriesPerPage, fromDate, toDate, search, reloadToken]);

  const totalPages = calculateTotalPages(totalCount, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const applySearch = () => {
    setCurrentPage(1);
    setSearch(searchInput.trim());
  };

  const handleView = async (id) => {
    try {
      const data = await getCareerApplicationById(id);
      const href = resumeHref(data.resumeUrl);
      const resumeLabel = data.resumeFileName || "View resume";
      const resumeHtml = href
        ? `<a class="bls-resume-icon" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" title="${escapeHtml(resumeLabel)}" aria-label="${escapeHtml(resumeLabel)}"><i class="ri-file-pdf-fill"></i></a>`
        : escapeHtml(data.resumeFileName);

      showDetailModal({
        title: data.subject || "Career Application",
        rows: [
          { label: "Name", value: data.fullName },
          { label: "Email", value: data.email },
          { label: "Phone", value: data.phoneNumber },
          { label: "Experience", value: data.experienceYears },
          { label: "Subject", value: data.subject },
          { label: "Message", value: data.message },
          { label: "Resume", html: resumeHtml },
          { label: "Page URL", html: externalLink(data.pageUrl) },
        ],
      });
    } catch (error) {
      handleErrors(error);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete("Career Application");
    if (!confirmed) return;

    try {
      await deleteCareerApplication(id);
      Swal.fire(
        "Deleted!",
        "The career application has been deleted successfully.",
        "success"
      );
      setReloadToken((token) => token + 1);
    } catch (error) {
      handleErrors(error);
    }
  };

  return (
    <>
      <style>
        {`
          .career-toolbar {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            gap: 16px;
            flex-wrap: wrap;
          }
          .career-toolbar .form-select-entry-count {
            margin-bottom: 0;
          }
          .career-toolbar-filters {
            display: flex;
            align-items: flex-end;
            gap: 12px;
            flex-wrap: wrap;
          }
          .career-toolbar-field .form-label {
            margin-bottom: 4px;
            font-size: 13px;
          }
          .career-toolbar-field .form-control {
            width: 160px;
          }
          .career-toolbar-search .form-control {
            width: 240px;
          }
          .career-toolbar-submit {
            height: 37.5px;
          }
          .career-resume-link {
            color: #d32f2f;
            font-size: 22px;
            line-height: 1;
            text-decoration: none;
          }
          .career-resume-link:hover {
            color: #b71c1c;
          }
          @media (max-width: 768px) {
            .career-toolbar,
            .career-toolbar-filters {
              align-items: stretch;
            }
            .career-toolbar-field .form-control,
            .career-toolbar-search .form-control,
            .career-toolbar-submit {
              width: 100%;
            }
          }
        `}
      </style>
      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header d-sm-flex align-items-center justify-content-between">
                <h5 className="mb-sm-2 mt-sm-2">Manage Career Applications</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <div className="career-toolbar mb-3">
                  <div className="career-toolbar-filters">
                    <div className="career-toolbar-field">
                      <label htmlFor="careerFromDate" className="form-label">
                        From Date
                      </label>
                      <Flatpickr
                        id="careerFromDate"
                        className="form-control bls-date-input"
                        placeholder="dd-mm-yyyy"
                        value={fromDate}
                        options={blsDatePickerOptions}
                        onChange={([date]) => {
                          setFromDate(date || "");
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                    <div className="career-toolbar-field">
                      <label htmlFor="careerToDate" className="form-label">
                        To Date
                      </label>
                      <Flatpickr
                        id="careerToDate"
                        className="form-control bls-date-input"
                        placeholder="dd-mm-yyyy"
                        value={toDate}
                        options={blsDatePickerOptions}
                        onChange={([date]) => {
                          setToDate(date || "");
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                    <div className="career-toolbar-field career-toolbar-search">
                      <label htmlFor="careerSearch" className="form-label">
                        Search
                      </label>
                      <input
                        id="careerSearch"
                        type="text"
                        placeholder="Name, email, phone, subject"
                        className="form-control"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") applySearch();
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary career-toolbar-submit"
                      onClick={applySearch}
                    >
                      Search
                    </button>
                  </div>
                  <EntriesDropdown
                    entriesPerPage={entriesPerPage}
                    onEntriesChange={handleEntriesChange}
                  />
                </div>

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "#",
                          "Name",
                          "Email",
                          "Phone",
                          "Experience",
                          "Subject",
                          "Resume",
                          "Added On",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {applications.length === 0 ? (
                          <TableDataStatusError colspan="9" />
                        ) : (
                          applications.map((item) => {
                            const href = resumeHref(item.resumeUrl);
                            return (
                              <tr className="manage-page-group" key={item.id}>
                                <td>{item.rowNumber}</td>
                                <td>{item.fullName || "-"}</td>
                                <td>{item.email || "-"}</td>
                                <td>{item.phoneNumber || "-"}</td>
                                <td>
                                  {item.experienceYears === 0 ||
                                  item.experienceYears
                                    ? item.experienceYears
                                    : "-"}
                                </td>
                                <td>{item.subject || "-"}</td>
                                <td>
                                  {href ? (
                                    <BootstrapTooltip
                                      title={item.resumeFileName || "View resume"}
                                    >
                                      <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="career-resume-link"
                                        aria-label={
                                          item.resumeFileName || "View resume"
                                        }
                                      >
                                        <i className="ri-file-pdf-fill"></i>
                                      </a>
                                    </BootstrapTooltip>
                                  ) : (
                                    item.resumeFileName || "-"
                                  )}
                                </td>
                                <td>{formatDateTime(item.addedOn)}</td>
                                <td>
                                  <BootstrapTooltip title="View">
                                    <span
                                      className="cursor-pointer me-2"
                                      onClick={() => handleView(item.id)}
                                    >
                                      <i className="ri-eye-fill"></i>
                                    </span>
                                  </BootstrapTooltip>
                                  {pageAccessDetails.deleteAccess && (
                                    <BootstrapTooltip title="Delete">
                                      <span
                                        className="cursor-pointer"
                                        onClick={() => handleDelete(item.id)}
                                      >
                                        <i className="ri-delete-bin-6-line"></i>
                                      </span>
                                    </BootstrapTooltip>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={totalCount}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
